import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "components/Layout/Card";
import UserProfileDetailsCard from "components/Layout/UserProfileDetailsCard";
import { useDispatch } from "react-redux";
import { fetchUserActivitySummary } from "store/slices";
import moment from "moment";
import Loader from "components/Layout/Loader";
import { getDataFromLocalStorage } from "utils/helpers";

const ProfileOverview = () => {
  const { '*': subPath, userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState({})
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch()
  const getgetUserActivitySummary = async () => {
    setLoading(true);
    try {
      const response = await dispatch(fetchUserActivitySummary(userId));
      if (response?.data) setUserData(response.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!['0', '1', '6'].includes(getDataFromLocalStorage('user_type'))) {
      navigate('/');
    }
    getgetUserActivitySummary()
  }, [userId])

  const { user_details, network_details, mentor, feedback, events_registered_members, chapters_and_groups, career_support, inbox_and_notification } = userData || {}


  const statsCard = (title, rows, viewAllPath = null, minHeight = "auto") => (
    <Card className="cp-20 cmt-10" style={{ minHeight }}>
      <div className="border rounded overflow-hidden">
        <div className="d-flex justify-content-between align-items-center text-14-500-18 bg-F3F4F8 cps-24 cpt-11 cpb-11 border-bottom">
          <span>{title}</span>
          {viewAllPath && (
            <span
              className="d-flex align-items-center gap-1 color-7cff pointer cme-10"
              onClick={() => navigate(viewAllPath)}
            >
              View All <i className="bi bi-chevron-right" style={{ fontSize: "12px" }} />
            </span>
          )}
        </div>

        {rows.map((row, idx) => (
          <div
            key={idx}
            className={`d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 ${idx !== rows.length - 1 ? "border-bottom" : ""
              }`}
          >
            <span className="text-14-400-18 color-black-olive">{row.label}</span>
            <span className="text-14-500-18">{row.value}</span>
          </div>
        ))}
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Loader size="sm" />
      </div>
    );
  }

  const userProfileData = {
    profile_photo_path: user_details?.profile_photo_path,
    user_name: user_details?.user_name,
    current_plan: user_details?.current_plan,
    email_id: user_details?.email_id,
    phone_number: user_details?.phone_number,
    country_name: user_details?.country_name,
    profile_completion: user_details?.profile_completion
  }

  return (
    <>
      {/* ---- Profile Details & Mentorship ---- */}
      <div className="row g-2 cmt-10">
        <div className="col-12 col-md-5 col-lg-4">
          <UserProfileDetailsCard
            user_details={userProfileData}
            isViewProfile
            onClick={() => navigate("view-profile")}
          />

          {statsCard("Mentorship", [
            {
              label: "Joined mentorship", value: mentor?.join_as_mentor
                ? moment(mentor.join_as_mentor).format('DD-MMM-YYYY') 
                : '-'

            },
            { label: "Sessions Created", value: mentor?.created_sessions || '-' },
          ])}
        </div>

        {/* ---- Attended Events Table ---- */}
        <div className="col-12 col-md-7 col-lg-8">
          <Card className="cps-20 cpe-20 cpt-20 cpb-10">
            <div className="border rounded overflow-hidden">
              <table className="table mb-0" style={{ minHeight: "355px" }}>
                <thead className="border-bottom bg-F3F4F8">
                  <tr>
                    {["S.No", "Attended Events", "Category", "Action"].map((h, i) => (
                      <th key={i} className="cps-24 cpt-11 cpb-11 text-14-500-18">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="text-14-400-18 color-black-olive">
                  {events_registered_members?.length > 0 ? (
                    events_registered_members.slice(0, 6).map((item, i) => (
                      <tr key={item.id || i}>
                        <td className="cps-24 cpt-11 cpb-11">{i + 1}</td>
                        <td className="cps-24 cpt-11 cpb-11 color-7cff">{item.event_name}</td>
                        <td className="cps-24 cpt-11 cpb-11">{item.category}</td>
                        <td
                          className="cps-24 cpt-11 cpb-11 cpe-36 text-center pointer"
                          onClick={() => navigate(`submitted-abstracts/${item?.event_id}`)}
                        >
                          <i className="bi bi-eye" style={{ fontSize: "18px" }}></i>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center cpb-20 cpt-20 text-muted">
                        No events found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {events_registered_members?.length > 6 && (
              <span
                className="d-flex align-items-center justify-content-center color-7cff pointer cmt-3"
                onClick={() => navigate("attended-events")}
              >
                View All
                <i className="bi bi-chevron-right ms-2" style={{ fontSize: "12px" }}></i>
              </span>
            )}
          </Card>

          {/* ---- Inbox & Posts ---- */}
          <div className="row g-2">
            <div className="col-12 col-md-6">
              {statsCard("Inbox & Notification", [
                { label: "Messages Sent", value: inbox_and_notification?.sended_messages || '-' },
                { label: "Messages Received", value: inbox_and_notification?.received_message || '-' },
                { label: "Messages Unread", value: inbox_and_notification?.unread_messages || '-' },
                { label: "Starred Messages", value: inbox_and_notification?.starred_messages || '-' },
                { label: "Notification", value: inbox_and_notification?.notification || '-' },
              ])}
            </div>

            <div className="col-12 col-md-6">
              {statsCard(
                "Post & Networks Details",
                [
                  { label: "Post created", value: network_details?.posts || '-' },
                  { label: "Likes", value: network_details?.likes || '-' },
                  { label: "Comments", value: network_details?.comments || '-' },
                  { label: "Followers", value: network_details?.followers || '-' },
                  { label: "Following", value: network_details?.followings || '-' },
                ],
                (network_details?.posts || network_details?.likes) ? "post-networks" : null,
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Last Row: 3 cards side by side ---- */}
      <div className="row g-2 ">
        <div className="col-12 col-md-4">
          {statsCard(
            "Chapters & Groups",
            [
              { label: "Chapters Followed", value: chapters_and_groups?.chapters_follower || '-' },
              { label: "Special Interest Community (SIC)", value: chapters_and_groups?.SIC || '-' },
            ],
            null,
            "152px"
          )}
        </div>

        <div className="col-12 col-md-4">
          {statsCard(
            "Career Support",
            [
              { label: "Job Applied", value: career_support?.job_applied || '-' },
              { label: "Build AI Resume", value: career_support?.resume_build_count || '-' },
            ],
            career_support?.job_applied ? "career-support" : null,
            "152px"
          )}
        </div>

        <div className="col-12 col-md-4">
          {statsCard(
            "Feedbacks",
            [{ label: "Feedbacks submitted", value: feedback?.total_feedback || '-' }],
            null,
            "152px"
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileOverview;


// import Card from 'components/Layout/Card'
// import UserProfileDetailsCard from 'components/Layout/UserProfileDetailsCard'
// import React from 'react'
// import { useNavigate } from 'react-router-dom'

// const ProfileOverview = ({ userData }) => {
//     const navigate = useNavigate()


//     return (
//         <>
//             <div className="row cmt-5 g-2">
//                 <div className="col-12 col-sm-12 col-md-5 col-lg-4">
//                     <UserProfileDetailsCard user_details={userData} isViewProfile onClick={() => navigate("view-profile")} />
//                     <Card className='cmt-10 cp-20'>
//                         <div className="border rounded overflow-hidden">

//                             {/* Header */}
//                             <div className="text-14-500-18 bg-F3F4F8 cps-24 cpt-11 cpb-11 border-bottom">
//                                 Mentorship
//                             </div>

//                             {/* Content Rows */}
//                             <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                 <span className="text-14-400-18 color-black-olive">Joined mentorship</span>
//                                 <span className="text-14-500-18 ">01-Jan-2025</span>
//                             </div>

//                             <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36">
//                                 <span className="text-14-400-18 color-black-olive">
//                                     Sessions Created
//                                 </span>
//                                 <span className="text-14-500-18">12</span>
//                             </div>
//                         </div>
//                     </Card>
//                 </div>

//                 <div className="col-12 col-sm-12 col-md-7 col-lg-8">
//                     <Card className='cp-20'>
//                         <div className="border rounded overflow-hidden">
//                             {/* Table */}
//                             <table className="table mb-0">
//                                 <thead className='border-bottom'>
//                                     <tr>
//                                         <th className='cps-24 cpt-11 cpb-11 text-14-500-18 bg-F3F4F8 '>S.No</th>
//                                         <th className='cps-24 cpt-11 cpb-11 text-14-500-18 bg-F3F4F8 '>Attended Events</th>
//                                         <th className='cps-24 cpt-11 cpb-11 text-14-500-18 bg-F3F4F8 '>Category</th>
//                                         <th className='cps-24 cpt-11 cpb-11 text-14-500-18 bg-F3F4F8 '>Action</th>
//                                     </tr>
//                                 </thead>

//                                 <tbody className="text-14-400-18 color-black-olive">
//                                     <tr>
//                                         <td className="cps-24 cpt-11 cpb-11">01</td>
//                                         <td className="cps-24 cpt-11 cpb-11 color-7cff">
//                                             4th Asia Pacific Conference on Educational Research

//                                         </td>
//                                         <td className="cps-24 cpt-11 cpb-11">Webinar</td>
//                                         <td className="cps-24 cpt-11 cpb-11 cpe-36">
//                                             <i className="bi bi-eye color-black-olive"></i>

//                                         </td>
//                                     </tr>

//                                     <tr>
//                                         <td className="cps-24 cpt-11 cpb-11">02</td>
//                                         <td className="cps-24 cpt-11 cpb-11 color-7cff">
//                                             Academic International Conference on Teaching Education
//                                         </td>
//                                         <td className="cps-24 cpt-11 cpb-11">Conference</td>
//                                         <td className="cps-24 cpt-11 cpb-11 cpe-36">
//                                             <i className="bi bi-eye color-black-olive"></i>

//                                         </td>
//                                     </tr>

//                                     <tr>
//                                         <td className="cps-24 cpt-11 cpb-11">03</td>
//                                         <td className="cps-24 cpt-11 cpb-11 color-7cff">
//                                             8th International Conference on Multi-Disciplinary
//                                         </td>
//                                         <td className="cps-24 cpt-11 cpb-11">Conference</td>
//                                         <td className="cps-24 cpt-11 cpb-11 cpe-36">
//                                             <i className="bi bi-eye color-black-olive"></i>

//                                         </td>
//                                     </tr>

//                                     <tr>
//                                         <td className="cps-24 cpt-11 cpb-11">04</td>
//                                         <td className="cps-24 cpt-11 cpb-11 color-7cff">
//                                             International Academic Conference on Teaching Education
//                                         </td>
//                                         <td className="cps-24 cpt-11 cpb-11">Webinar</td>
//                                         <td className="cps-24 cpt-11 cpb-11 cpe-36">
//                                             <i className="bi bi-eye color-black-olive"></i>

//                                         </td>
//                                     </tr>

//                                     <tr>
//                                         <td className="cps-24 cpt-11 cpb-11">05</td>
//                                         <td className="cps-24 cpt-11 cpb-11 color-7cff">
//                                             4th Asia Pacific Conference on Educational Research
//                                         </td>
//                                         <td className="cps-24 cpt-11 cpb-11">Webinar</td>
//                                         <td className="cps-24 cpt-11 cpb-11 cpe-36">
//                                             <i className="bi bi-eye color-black-olive"></i>

//                                         </td>
//                                     </tr>

//                                     <tr>
//                                         <td className="cps-24 cpt-11 cpb-11">06</td>
//                                         <td className="cps-24 cpt-11 cpb-11 color-7cff">
//                                             International Academic Conference on Teaching
//                                         </td>
//                                         <td className="cps-24 cpt-11 cpb-11">Conference</td>
//                                         <td className="cps-24 cpt-11 cpb-11 cpe-36">
//                                             <i className="bi bi-eye color-black-olive"></i>

//                                         </td>
//                                     </tr>
//                                 </tbody>

//                             </table>

//                         </div>
//                         <span className="d-flex align-items-center justify-content-center color-7cff pointer cmt-5" onClick={() => navigate('attended-events')}>
//                             View All <i className="bi bi-chevron-right" style={{ fontSize: '12px' }} />
//                         </span>
//                     </Card>


//                     <div className='row g-2 cmt-5'>
//                         <div className="col-12 col-sm-12 col-md-6 col-lg-6">
//                             <Card className='cp-20'>
//                                 <div className="border rounded overflow-hidden">

//                                     {/* Header */}
//                                     <div className="text-14-500-18 bg-F3F4F8 cps-24 cpt-11 cpb-11 border-bottom">
//                                         Inbox & Notification
//                                     </div>

//                                     {/* Content Rows */}
//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                         <span className="text-14-400-18 color-black-olive">Messages Sent</span>
//                                         <span className="text-14-500-18 ">2,355</span>
//                                     </div>

//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                         <span className="text-14-400-18 color-black-olive">
//                                             Messages Received
//                                         </span>
//                                         <span className="text-14-500-18">3,435</span>
//                                     </div>

//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                         <span className="text-14-400-18 color-black-olive">
//                                             Messages Unread
//                                         </span>
//                                         <span className="text-14-500-18">16</span>
//                                     </div>

//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                         <span className="text-14-400-18 color-black-olive">
//                                             Starred Messages
//                                         </span>
//                                         <span className="text-14-500-18">46</span>
//                                     </div>

//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36">
//                                         <span className="text-14-400-18 color-black-olive">
//                                             Notification
//                                         </span>
//                                         <span className="text-14-500-18">1,234</span>
//                                     </div>
//                                 </div>
//                             </Card>
//                         </div>

//                         <div className="col-12 col-sm-12 col-md-6 col-lg-6">
//                             <Card className='cp-20'>
//                                 <div className="border rounded overflow-hidden">

//                                     {/* Header */}
//                                     <div className="d-flex justify-content-between align-items-center text-14-500-18 bg-F3F4F8 cps-24 cpt-11 cpb-11 border-bottom">
//                                         <span>Post & Networks Details</span>
//                                         <span className="d-flex align-items-center gap-1 color-7cff pointer cme-10" onClick={() => navigate('post-networks')}>
//                                             View All <i className="bi bi-chevron-right" style={{ fontSize: '12px' }} />
//                                         </span>
//                                     </div>


//                                     {/* Content Rows */}
//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                         <span className="text-14-400-18 color-black-olive">Post created</span>
//                                         <span className="text-14-500-18 ">14</span>
//                                     </div>

//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                         <span className="text-14-400-18 color-black-olive">
//                                             Likes
//                                         </span>
//                                         <span className="text-14-500-18">233</span>
//                                     </div>

//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                         <span className="text-14-400-18 color-black-olive">
//                                             Comments
//                                         </span>
//                                         <span className="text-14-500-18">16</span>
//                                     </div>

//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                         <span className="text-14-400-18 color-black-olive">
//                                             Followers
//                                         </span>
//                                         <span className="text-14-500-18">563</span>
//                                     </div>

//                                     <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36">
//                                         <span className="text-14-400-18 color-black-olive">
//                                             Following
//                                         </span>
//                                         <span className="text-14-500-18">1,234</span>
//                                     </div>
//                                 </div>
//                             </Card>
//                         </div>

//                     </div>
//                 </div>


//             </div>
//             <div className='row g-2'>
//                 <div className="col-12 col-sm-12 col-md-4 col-lg-4">
//                     <Card className="cmt-10 cp-20" style={{ minHeight: "152px" }}>
//                         <div className="border rounded overflow-hidden">

//                             {/* Header */}
//                             <div className="text-14-500-18 bg-F3F4F8 cps-24 cpt-11 cpb-11 border-bottom">
//                                 Chapters & Groups
//                             </div>

//                             {/* Content Rows */}
//                             <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                 <span className="text-14-400-18 color-black-olive">Chapters Followed</span>
//                                 <span className="text-14-500-18 ">14</span>
//                             </div>

//                             <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36">
//                                 <span className="text-14-400-18 color-black-olive">
//                                     Special Interest Community (SIC)
//                                 </span>
//                                 <span className="text-14-500-18">03</span>
//                             </div>
//                         </div>
//                     </Card>
//                 </div>

//                 <div className="col-12 col-sm-12 col-md-4 col-lg-4">
//                     <Card className='cmt-10 cp-20' style={{ minHeight: "152px" }}>
//                         <div className="border rounded overflow-hidden">

//                             {/* Header */}
//                             <div className="d-flex justify-content-between align-items-center text-14-500-18 bg-F3F4F8 cps-24 cpt-11 cpb-11 border-bottom">
//                                 <span>Career Support</span>
//                                 <span className="d-flex align-items-center gap-1 color-7cff pointer cme-10" onClick={() => navigate('career-support')}>
//                                     View All <i className="bi bi-chevron-right" style={{ fontSize: '12px' }} />
//                                 </span>
//                             </div>


//                             {/* Content Rows */}
//                             <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                 <span className="text-14-400-18 color-black-olive">Job Applied</span>
//                                 <span className="text-14-500-18 ">100</span>
//                             </div>

//                             <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36">
//                                 <span className="text-14-400-18 color-black-olive">
//                                     Build AI Resume
//                                 </span>
//                                 <span className="text-14-500-18">08</span>
//                             </div>
//                         </div>
//                     </Card>
//                 </div>
//                 <div className="col-12 col-sm-12 col-md-4 col-lg-4">
//                     <Card className='cmt-10 cp-20' style={{ minHeight: "152px" }}>
//                         <div className="border rounded overflow-hidden">

//                             {/* Header */}
//                             <div className="text-14-500-18 bg-F3F4F8 cps-24 cpt-11 cpb-11 border-bottom">
//                                 Feedbacks
//                             </div>

//                             {/* Content Rows */}
//                             <div className="d-flex justify-content-between align-items-center cps-24 cpt-11 cpb-11 cpe-36 border-bottom">
//                                 <span className="text-14-400-18 color-black-olive">Feedbacks submitted</span>
//                                 <span className="text-14-500-18 ">0</span>
//                             </div>


//                         </div>
//                     </Card>
//                 </div>

//             </div>
//         </>
//     )
// }

// export default ProfileOverview
